import dayjs from "dayjs";
import { FaTimes, FaAmbulance, FaUserMd } from "react-icons/fa";
import { TbActivity } from "react-icons/tb";

const AVATAR_COLORS = [
  "#EF4444",
  "#F59E0B",
  "#6366F1",
  "#22C55E",
  "#8B5CF6",
  "#06B6D4",
  "#EC4899",
  "#14B8A6",
];

const getAvatarBg = (name) => {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const formatAgeGender = (p) => {
  let age = p.age;
  if ((!age || age === 0 || age === "0") && p.dob) {
    const birthYear = dayjs(p.dob, ["MM/DD/YYYY", "YYYY-MM-DD", "DD/MM/YYYY"]);
    if (birthYear.isValid()) {
      age = dayjs().diff(birthYear, "year");
    }
  }
  const ageStr = age && age > 0 ? `${age} Y` : "";
  let genderStr = "";
  if (p.gender) {
    const g = String(p.gender).toUpperCase();
    if (g === "F" || g === "FEMALE") genderStr = "Female";
    else if (g === "M" || g === "MALE") genderStr = "Male";
    else genderStr = p.gender;
  }
  if (ageStr && genderStr) return `${ageStr} / ${genderStr}`;
  if (ageStr) return ageStr;
  if (genderStr) return genderStr;
  return p.ageGender || "-";
};

const formatArrivalTime = (p) => {
  if (p.arrivalDate) {
    const formattedDate = dayjs(p.arrivalDate).isValid()
      ? dayjs(p.arrivalDate).format("DD/MM/YYYY")
      : p.arrivalDate;
    let formattedTime = "";
    if (p.arrivalTime) {
      const timeObj = dayjs(`2000-01-01 ${p.arrivalTime}`);
      formattedTime = timeObj.isValid()
        ? timeObj.format("hh:mm A")
        : p.arrivalTime;
    }
    return formattedTime ? `${formattedDate}\n${formattedTime}` : formattedDate;
  }
  if (p.createdDate) {
    return dayjs(p.createdDate).isValid()
      ? dayjs(p.createdDate).format("DD/MM/YYYY\nhh:mm A")
      : p.createdDate;
  }
  return p.arrivalTime || "-";
};

const formatDoctor = (p) => {
  if (p.doctor) return p.doctor;
  if (p.userName) {
    return p.userName.startsWith("Dr.") ? p.userName : `Dr. ${p.userName}`;
  }
  if (p.doctorId) {
    return `Dr. ${p.doctorId}`;
  }
  return "Emergency Dept";
};

const formatWardBed = (p) => {
  const ward = p.wardId || p.wardCode || "";
  const bed = p.bedId || p.bedCode || p.bedNumber || "";
  if (ward && bed) return `${ward} / ${bed}`;
  if (ward) return ward;
  if (bed) return `Bed ${bed}`;
  return p.wardBed || "-";
};

export default function EmergencyDetailModal({
  patient,
  onClose,
  onNavigateInpatient,
}) {
  if (!patient) return null;

  const patientName = patient.patientName || patient.name || "Patient";
  const avatarLetter = (patientName.charAt(0) || "P").toUpperCase();
  const avatarBg = patient.avatarBg || getAvatarBg(patientName);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* ── Modal Header ── */}
        <div className="p-6 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center text-white text-base font-bold flex-shrink-0"
              style={{ backgroundColor: avatarBg }}
            >
              {avatarLetter}
            </div>
            <div>
              <h3 className="text-lg font-bold text-gray-800">{patientName}</h3>
              <p className="text-xs text-gray-500">
                Emergency ID: {patient.emergencyId || patient.id || "-"} |
                Patient ID: {patient.patientId || "-"}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100 transition"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* ── Modal Body ── */}
        <div className="p-6 space-y-5 text-sm">
          {/* Status & Triage Row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 bg-gray-50 p-3 rounded-xl border">
            <div>
              <span className="text-[11px] text-gray-400 block">Status</span>
              <span className="font-semibold text-gray-800">
                {(patient.status || "REGISTERED").replace(/_/g, " ")}
              </span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block">
                Triage Level
              </span>
              <span
                className={`inline-block px-2 py-0.5 rounded text-xs font-bold ${
                  patient.triageLevel === "HIGH"
                    ? "bg-red-100 text-red-700"
                    : patient.triageLevel === "MEDIUM"
                    ? "bg-amber-100 text-amber-700"
                    : "bg-green-100 text-green-700"
                }`}
              >
                {patient.triageLevel || "Standard"}
              </span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block">
                Ward / Bed
              </span>
              <span className="font-semibold text-gray-800">
                {formatWardBed(patient)}
              </span>
            </div>
            <div>
              <span className="text-[11px] text-gray-400 block">Doctor</span>
              <span className="font-semibold text-gray-800">
                {formatDoctor(patient)}
              </span>
            </div>
          </div>

          {/* Vitals Section */}
          <div>
            <h4 className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2 flex items-center gap-1.5">
              <TbActivity className="text-teal-600 text-base" /> Patient Vitals
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="border rounded-xl p-3 bg-white">
                <span className="text-xs text-gray-400 block">
                  Blood Pressure
                </span>
                <span className="text-base font-bold text-gray-800">
                  {patient.bp || "-"}
                </span>
              </div>
              <div className="border rounded-xl p-3 bg-white">
                <span className="text-xs text-gray-400 block">Pulse Rate</span>
                <span className="text-base font-bold text-gray-800">
                  {patient.pulse ? `${patient.pulse} bpm` : "-"}
                </span>
              </div>
              <div className="border rounded-xl p-3 bg-white">
                <span className="text-xs text-gray-400 block">SpO2</span>
                <span className="text-base font-bold text-gray-800">
                  {patient.spo2 ? `${patient.spo2}%` : "-"}
                </span>
              </div>
              <div className="border rounded-xl p-3 bg-white">
                <span className="text-xs text-gray-400 block">Temperature</span>
                <span className="text-base font-bold text-gray-800">
                  {patient.temperature ? `${patient.temperature} °F` : "-"}
                </span>
              </div>
            </div>
          </div>

          {/* Chief Complaint */}
          <div className="border rounded-xl p-3 bg-white">
            <span className="text-xs text-gray-400 block mb-1">
              Chief Complaint
            </span>
            <p className="text-sm font-medium text-gray-800">
              {patient.complaint ||
                patient.chiefComplaint ||
                "None reported"}
            </p>
          </div>

          {/* Demographics & Contact */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">Age & Gender</span>
              <span className="text-sm font-semibold text-gray-800">
                {formatAgeGender(patient)}
              </span>
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">Mobile Number</span>
              <span className="text-sm font-semibold text-gray-800">
                {patient.mobile || "-"}
              </span>
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">
                Emergency Contact
              </span>
              <span className="text-sm font-semibold text-gray-800">
                {patient.emergencyContact || "-"}
              </span>
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">Blood Group</span>
              <span className="text-sm font-semibold text-gray-800">
                {patient.bloodGroup || "-"}
              </span>
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">
                Arrival Date & Time
              </span>
              <span className="text-sm font-semibold text-gray-800 whitespace-pre-line">
                {formatArrivalTime(patient)}
              </span>
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <span className="text-xs text-gray-400 block">Patient Type</span>
              <span className="text-sm font-semibold text-gray-800">
                {patient.patientType === "N"
                  ? "New"
                  : patient.patientType === "E"
                  ? "Existing"
                  : patient.patientType || "-"}
              </span>
            </div>
          </div>

          {/* Ambulance & Transit Details */}
          {(patient.ambulanceNumber ||
            patient.driverName ||
            patient.pickupLocation) && (
            <div className="border rounded-xl p-3 bg-amber-50/50 border-amber-200">
              <h5 className="text-xs font-bold text-amber-800 mb-2 flex items-center gap-1.5">
                <FaAmbulance className="text-amber-600" /> Transit & Transport
                Information
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
                <div>
                  <span className="text-gray-500 block">Ambulance No:</span>
                  <span className="font-semibold text-gray-800">
                    {patient.ambulanceNumber || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Driver Name:</span>
                  <span className="font-semibold text-gray-800">
                    {patient.driverName || "-"}
                  </span>
                </div>
                <div>
                  <span className="text-gray-500 block">Pickup Location:</span>
                  <span className="font-semibold text-gray-800">
                    {patient.pickupLocation || "-"}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Modal Footer ── */}
        <div className="p-4 border-t bg-gray-50 rounded-b-2xl flex items-center justify-between gap-3 flex-wrap">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-semibold text-gray-600 hover:bg-gray-200 rounded-lg transition"
          >
            Close
          </button>
          {patient.patientId && (
            <button
              onClick={() => {
                onClose();
                if (onNavigateInpatient) onNavigateInpatient(patient);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-[#189FAA] text-white text-sm font-semibold rounded-lg hover:bg-teal-700 transition shadow-sm"
            >
              <FaUserMd />
              <span>View Inpatient Details</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
